import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input, Select } from '../components/ui/Input';
import { PlayIcon, TrendingUpIcon, UsersIcon, TargetIcon, RocketIcon, BarChartIcon, CheckCircle2Icon, StarIcon, CalendarIcon, ArrowRightIcon, VolumeX } from 'lucide-react';
import { initPixel, trackCtaClick, trackVideoPlay, trackLead, trackFormStart } from '../utils/metaPixel';
function AnimatedNumber({
  value,
  prefix = '',
  suffix = '',
  duration = 1.5
}: {
  value: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!isInView) return;
    const start = performance.now();
    const step = (now: number) => {
      const progress = Math.min((now - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      setDisplay(Math.round(eased * value));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [isInView, value, duration]);
  return <span ref={ref} data-id="element-15">
      {prefix}
      {display.toLocaleString()}
      {suffix}
    </span>;
}
export function LandingPage() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [formInteracted, setFormInteracted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  useEffect(() => {
    document.title = 'School Launch';
    initPixel();
  }, []);
  const handleUnmute = () => {
    if (videoRef.current) {
      videoRef.current.muted = false;
      videoRef.current.currentTime = 0;
      setIsMuted(false);
      trackVideoPlay();
    }
  };
  const handleCtaClick = (ctaName: string) => {
    trackCtaClick(ctaName);
  };
  const handleFormFocus = () => {
    if (!formInteracted) {
      setFormInteracted(true);
      trackFormStart();
    }
  };
  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(false);
    const form = e.currentTarget;
    const formData = new FormData(form);
    const urlParams = new URLSearchParams();
    urlParams.append('full_name', formData.get('full_name') as string);
    urlParams.append('school_name', formData.get('school_name') as string);
    urlParams.append('email', formData.get('email') as string);
    urlParams.append('phone', formData.get('phone') as string);
    urlParams.append('enrollment_capacity', formData.get('enrollment_capacity') as string);
    try {
      await fetch('https://services.leadconnectorhq.com/hooks/OtyTgdaoNhRuZomfMmhH/webhook-trigger/9943c17c-6c4f-4bcc-93c3-ccebd1663f11', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: urlParams.toString(),
        mode: 'no-cors'
      });
      trackLead();
      setIsSubmitted(true);
    } catch (err) {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="min-h-screen bg-black text-white font-sans selection:bg-amber-500 selection:text-black overflow-x-hidden" data-id="element-16">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-black/80 backdrop-blur-md border-b border-zinc-800" data-id="element-17">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-center md:justify-between" data-id="element-18">
          <a href="#" className="flex items-center" aria-label="SchoolLaunch - Home" data-id="element-19">
            <img src="https://cdn.magicpatterns.com/uploads/3aM3DA6U6f25P9EHkUZYxu/SCHOOL_LAUNCH_LOGO_black_2.png" alt="SchoolLaunch" className="h-10 w-auto" data-id="element-20" />
          </a>
          <Button variant="primary" size="sm" className="hidden md:flex" onClick={() => handleCtaClick('nav_apply')} data-id="element-21">
            Apply Now to See If You Qualify
          </Button>
        </div>
      </nav>

      {/* 1. Hero Section */}
      <section className="md:pt-32 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pt-[100px] pb-[20px]" data-id="element-22">
        <div className="text-center max-w-4xl mx-auto mb-8 md:mb-12" data-id="element-23">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 rounded-full bg-amber-500/10 text-amber-500 font-medium text-xs md:text-sm mb-4 md:mb-8 border border-amber-500/20" data-id="element-24">
            Exclusive for Learning Centers Looking to Grow
          </div>

          <h1 className="text-3xl sm:text-5xl md:text-7xl font-extrabold tracking-tight mb-3 md:mb-6 leading-tight" data-id="element-25">
            Is Your School
            <br className="sm:hidden" data-id="element-26" /> Losing Students to{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600 whitespace-nowrap" data-id="element-27">
              Empty Seats?
            </span>
          </h1>

          <p className="text-base md:text-2xl text-zinc-400 mb-6 md:mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-28">
            We help learning centers add 40+ students in 90 days using our
            proven "Parent-First" marketing system.
          </p>

          {/* VSL Video */}
          <div className="relative w-full max-w-4xl mx-auto aspect-video bg-zinc-900 rounded-2xl border border-zinc-800 shadow-2xl overflow-hidden mb-6 md:mb-12 group" data-id="element-29">
            <video ref={videoRef} className="w-full h-full object-cover" autoPlay muted={isMuted} loop playsInline controls={!isMuted} preload="metadata" data-id="element-30">
              <source src="https://cdn.magicpatterns.com/uploads/29qEefnVojXxrob6CqnZT6/School Launch VSL.mp4" type="video/mp4" data-id="element-31" />
              Your browser does not support the video tag.
            </video>

            {isMuted && <div className="absolute inset-0 cursor-pointer flex flex-col justify-end p-4 md:p-6 bg-gradient-to-t from-black/80 via-transparent to-transparent" onClick={handleUnmute} data-id="element-32">
                <div className="flex items-center gap-2 md:gap-3 bg-black/60 backdrop-blur-md border border-white/10 text-white px-3 py-2 md:px-4 md:py-2.5 rounded-full w-fit hover:bg-black/80 transition-colors" data-id="element-33">
                  <div className="relative flex h-3 w-3 md:h-4 md:w-4 items-center justify-center" data-id="element-34">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" data-id="element-35"></span>
                    <VolumeX className="h-3 w-3 md:h-4 md:w-4 relative z-10 text-amber-500" data-id="element-36" />
                  </div>
                  <span className="font-medium text-xs md:text-sm" data-id="element-37">
                    Click to Unmute
                  </span>
                </div>
              </div>}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-6 mb-8 md:mb-12" data-id="element-38">
            <Button size="lg" className="w-full sm:w-auto text-base md:text-lg group" onClick={() => handleCtaClick('hero_apply')} data-id="element-39">
              Apply Now to See if You Qualify
              <ArrowRightIcon className="ml-2 h-4 w-4 md:h-5 md:w-5 group-hover:translate-x-1 transition-transform" data-id="element-40" />
            </Button>
          </div>
        </div>
      </section>

      {/* 3. Solution Section */}
      <section className="py-16 md:py-24 pt-[30px]" data-id="element-41">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-42">
          <div className="text-center mb-10 md:mb-16" data-id="element-43">
            <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-44">
              The "Parent-First" Enrollment System
            </h2>
            <p className="text-xl text-zinc-400 max-w-2xl mx-auto" data-id="element-45">
              We don't just run ads. We build a predictable machine that turns
              local parents into enrolled families.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-46">
            <div className="relative" data-id="element-47">
              <div className="absolute top-0 left-6 -ml-px h-full w-0.5 bg-zinc-800 hidden md:block" data-id="element-48"></div>
              <div className="rounded-xl border p-6 bg-zinc-900 border-zinc-800 text-white shadow-md shadow-black/20 relative z-10 h-full backdrop-blur" data-id="element-49">
                <div className="w-12 h-12 bg-amber-500 text-black rounded-full flex items-center justify-center font-bold text-xl mb-6" data-id="element-50">
                  1
                </div>

                <h3 className="text-2xl font-bold mb-3" data-id="element-51">
                  Hyper-Local Targeting
                </h3>
                <p className="text-zinc-400" data-id="element-52">
                  We pinpoint parents within a 5-10 mile radius of your center
                  who are actively looking for premium childcare solutions right
                  now.
                </p>
              </div>
            </div>

            <div className="relative" data-id="element-53">
              <div className="absolute top-0 left-6 -ml-px h-full w-0.5 bg-zinc-800 hidden md:block" data-id="element-54"></div>
              <div className="rounded-xl border p-6 bg-zinc-900 border-zinc-800 text-white shadow-md shadow-black/20 relative z-10 h-full backdrop-blur" data-id="element-55">
                <div className="w-12 h-12 bg-amber-500 text-black rounded-full flex items-center justify-center font-bold text-xl mb-6" data-id="element-56">
                  2
                </div>

                <h3 className="text-2xl font-bold mb-3" data-id="element-57">Automated Nurturing</h3>
                <p className="text-zinc-400" data-id="element-58">
                  Our system automatically follows up with leads via SMS and
                  email within 5 minutes, booking them directly into your tour
                  calendar.
                </p>
              </div>
            </div>

            <div className="relative" data-id="element-59">
              <div className="rounded-xl border p-6 bg-zinc-900 border-zinc-800 text-white shadow-md shadow-black/20 relative z-10 h-full backdrop-blur" data-id="element-60">
                <div className="w-12 h-12 bg-amber-500 text-black rounded-full flex items-center justify-center font-bold text-xl mb-6" data-id="element-61">
                  3
                </div>

                <h3 className="text-2xl font-bold mb-3" data-id="element-62">Predictable Tours</h3>
                <p className="text-zinc-400" data-id="element-63">
                  You wake up to a calendar full of qualified tour appointments.
                  All you have to do is show them your amazing facility and
                  enroll them.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-12" data-id="element-64">
            <a href="#apply" onClick={() => handleCtaClick('solution_apply')} className="inline-flex items-center justify-center font-bold rounded-md text-base sm:text-lg px-8 py-4 bg-amber-500 text-black hover:bg-amber-400 shadow-lg shadow-amber-500/20 hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-black" data-id="element-65">
              Apply Now to See If You Qualify
              <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-66" />
            </a>
          </div>
        </div>
      </section>

      {/* 4. Proof Section */}
      <section className="py-16 md:py-24 bg-zinc-950 border-y border-zinc-900 pt-[30px]" data-id="element-67">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-68">
          <div className="max-w-2xl mx-auto text-center" data-id="element-69">
            <div data-id="element-70">
              <h2 className="text-3xl md:text-5xl font-bold mb-6 text-center" data-id="element-71">
                Don't just take our word for it.
              </h2>
              <p className="text-xl text-zinc-400 mb-10" data-id="element-72">
                We've helped dozens of centers reach full capacity and start
                waitlists.
              </p>
              <div className="space-y-6" data-id="element-73">
                <motion.div className="flex items-center gap-4" initial={{
                opacity: 0,
                y: 16
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-50px'
              }} transition={{
                duration: 0.4
              }} data-id="element-74">
                  <div className="text-4xl font-bold text-amber-500 tabular-nums" data-id="element-75">
                    <AnimatedNumber value={42} duration={1.2} data-id="element-76" />
                  </div>
                  <div className="text-lg text-zinc-300" data-id="element-77">
                    Average new enrollments in first 90 days
                  </div>
                </motion.div>
                <div className="w-full h-px bg-zinc-800" data-id="element-78"></div>
                <motion.div className="flex items-center gap-4" initial={{
                opacity: 0,
                y: 16
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-50px'
              }} transition={{
                duration: 0.4,
                delay: 0.15
              }} data-id="element-79">
                  <div className="text-4xl font-bold text-amber-500 tabular-nums" data-id="element-80">
                    <AnimatedNumber value={14} prefix="$" suffix="k" duration={1.4} data-id="element-81" />
                  </div>
                  <div className="text-lg text-zinc-300" data-id="element-82">
                    Average monthly recurring revenue added
                  </div>
                </motion.div>
                <div className="w-full h-px bg-zinc-800" data-id="element-83"></div>
                <motion.div className="flex items-center gap-4" initial={{
                opacity: 0,
                y: 16
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-50px'
              }} transition={{
                duration: 0.4,
                delay: 0.3
              }} data-id="element-84">
                  <div className="text-4xl font-bold text-amber-500 tabular-nums" data-id="element-85">
                    <AnimatedNumber value={50} suffix="%" duration={1.6} data-id="element-86" />
                  </div>
                  <div className="text-lg text-zinc-300" data-id="element-87">
                    Tour-to-enrollment conversion rate
                  </div>
                </motion.div>
              </div>

              <div className="mt-10 text-center" data-id="element-88">
                <a href="#apply" onClick={() => handleCtaClick('proof_apply')} className="inline-flex items-center justify-center font-bold rounded-md text-base sm:text-lg px-8 py-4 bg-amber-500 text-black hover:bg-amber-400 shadow-lg shadow-amber-500/20 hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-zinc-950" data-id="element-89">
                  Apply Now to See If You Qualify
                  <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-90" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Testimonials Section */}
      <section className="py-16 md:py-24 pt-[30px] pb-[30px]" data-id="element-91">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-92">
          <div className="text-center mb-10 md:mb-16" data-id="element-93">
            <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-94">
              Hear It Directly From Our Clients
            </h2>
            <p className="text-xl text-zinc-400 max-w-2xl mx-auto" data-id="element-95">
              Real school directors sharing their experience working with School
              Launch.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-96">
            {/* Testimonial 1 */}
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-zinc-800 bg-zinc-900 shadow-lg group" data-id="element-97">
              <img src="https://cdn.magicpatterns.com/uploads/4bj2Zxk9DjeU2D6WE1RorN/Captura_2026-04-07_164547_1.30.1.png" alt="Testimonial - Maria Lopez" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-98" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-transparent opacity-90" data-id="element-99"></div>
              <div className="absolute inset-0 p-8 flex flex-col justify-end" data-id="element-100">
                <blockquote className="text-xl md:text-2xl font-medium italic text-white mb-6 leading-snug" data-id="element-101">
                  "Within 60 days, we had to start a waitlist. It completely
                  changed our trajectory."
                </blockquote>
                <div data-id="element-102">
                  <div className="font-bold text-amber-500 text-lg" data-id="element-103">
                    Maria Lopez
                  </div>
                  <div className="text-sm text-zinc-400 font-medium" data-id="element-104">
                    Director, Bright Futures Preschool
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-zinc-800 bg-zinc-900 shadow-lg group" data-id="element-105">
              <img src="https://cdn.magicpatterns.com/uploads/chtPxitLYxXx7XZ5YTsLJ4/Captura_2026-04-07_164547_1.17.1.png" alt="Testimonial - Elena Rodriguez" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-106" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-transparent opacity-90" data-id="element-107"></div>
              <div className="absolute inset-0 p-8 flex flex-col justify-end" data-id="element-108">
                <blockquote className="text-xl md:text-2xl font-medium italic text-white mb-6 leading-snug" data-id="element-109">
                  "We stopped wasting money on ads that didn't work. The ROI has
                  been incredible."
                </blockquote>
                <div data-id="element-110">
                  <div className="font-bold text-amber-500 text-lg" data-id="element-111">
                    Elena Rodriguez
                  </div>
                  <div className="text-sm text-zinc-400 font-medium" data-id="element-112">
                    Owner, Little Stars Academy
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-zinc-800 bg-zinc-900 shadow-lg group" data-id="element-113">
              <img src="https://cdn.magicpatterns.com/uploads/pCFJREUSiV61qPU18wuPar/Captura_2026-04-07_164547_1.26.1.png" alt="Testimonial - Patricia Williams" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-114" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-transparent opacity-90" data-id="element-115"></div>
              <div className="absolute inset-0 p-8 flex flex-col justify-end" data-id="element-116">
                <blockquote className="text-xl md:text-2xl font-medium italic text-white mb-6 leading-snug" data-id="element-117">
                  "I finally have time to focus on my teachers and students
                  instead of marketing."
                </blockquote>
                <div data-id="element-118">
                  <div className="font-bold text-amber-500 text-lg" data-id="element-119">
                    Patricia Williams
                  </div>
                  <div className="text-sm text-zinc-400 font-medium" data-id="element-120">
                    Director, Sunshine Learning Center
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Calendar Booking Placeholder */}
      <section className="py-16 md:py-24 bg-zinc-950 border-y border-zinc-900 hidden" data-id="element-121">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-122">
          <h2 className="text-3xl font-bold mb-4" data-id="element-123">
            Step 2: Pick a time for your call
          </h2>
          <p className="text-zinc-400 mb-8" data-id="element-124">
            Select a time below that works best for you.
          </p>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 min-h-[600px] flex items-center justify-center" data-id="element-125">
            <div className="flex flex-col items-center text-zinc-500" data-id="element-126">
              <CalendarIcon className="h-12 w-12 mb-4 opacity-50" data-id="element-127" />
              <p data-id="element-128">Calendar Widget Embed Goes Here</p>
            </div>
          </div>
        </div>
      </section>

      {/* 8. Final CTA */}
      <section className="py-16 md:py-32 bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 text-center relative overflow-hidden" role="region" aria-label="Call to action" data-id="element-129">
        <div className="absolute inset-0 opacity-[0.04] bg-[radial-gradient(circle_at_center,black_1.5px,transparent_1.5px)] bg-[size:16px_16px]" aria-hidden="true" data-id="element-130"></div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-131">
          <h2 className="text-4xl md:text-6xl font-extrabold mb-6 text-black leading-tight" data-id="element-132">
            Stop losing money on empty seats.
          </h2>
          <p className="text-lg md:text-2xl font-medium mb-10 text-amber-950 max-w-2xl mx-auto leading-relaxed" data-id="element-133">
            Apply now and let us show you exactly how we'll pack your center
            with new enrollments.
          </p>
          <a href="#apply" onClick={() => handleCtaClick('final_cta_apply')} className="inline-flex items-center justify-center font-bold rounded-md text-sm px-6 py-3 sm:text-lg sm:px-10 sm:py-5 bg-black text-white hover:bg-zinc-900 shadow-2xl shadow-black/25 hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 focus:ring-offset-amber-500" data-id="element-134">
            Apply Now to See If You Qualify
            <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-135" />
          </a>
          <p className="mt-6 text-sm font-semibold text-amber-900 tracking-wide" data-id="element-136">
            No obligation. Just pure value.
          </p>
        </div>
      </section>

      {/* 7. Checklist Section */}
      <section className="py-16 md:py-24 pt-[30px] pb-[30px]" data-id="element-137">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-138">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-139">
            <div data-id="element-140">
              <h2 className="text-3xl md:text-4xl font-bold mb-8" data-id="element-141">
                Who this is for:
              </h2>
              <ul className="space-y-6" data-id="element-142">
                {[{
                title: 'Established Centers',
                desc: 'You have a great facility and curriculum, you just need more parents to see it.'
              }, {
                title: 'Capacity to Grow',
                desc: 'You have at least 15-20 empty spots you want to fill in the next 90 days.'
              }, {
                title: 'Ready to Invest',
                desc: 'You understand that marketing is an investment that yields a return, not an expense.'
              }].map((item, i) => <motion.li key={item.title} className="flex items-start gap-4" initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true,
                margin: '-50px'
              }} transition={{
                duration: 0.4,
                delay: i * 0.15,
                ease: 'easeOut'
              }} data-id="element-143">
                    <motion.div className="shrink-0 mt-1" initial={{
                  scale: 0,
                  rotate: -90
                }} whileInView={{
                  scale: 1,
                  rotate: 0
                }} viewport={{
                  once: true,
                  margin: '-50px'
                }} transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 15,
                  delay: i * 0.15 + 0.2
                }} data-id="element-144">
                      <CheckCircle2Icon className="h-6 w-6 text-amber-500" data-id="element-145" />
                    </motion.div>
                    <div data-id="element-146">
                      <h4 className="text-xl font-bold mb-1" data-id="element-147">{item.title}</h4>
                      <p className="text-zinc-400" data-id="element-148">{item.desc}</p>
                    </div>
                  </motion.li>)}
              </ul>
            </div>

            <div data-id="element-149">
              <h2 className="text-3xl md:text-4xl font-bold mb-8" data-id="element-150">
                Who this is NOT for:
              </h2>
              <ul className="space-y-6" data-id="element-151">
                {[{
                title: 'Brand New Startups',
                desc: "If you haven't opened your doors yet or lack basic licensing, we can't help you yet."
              }, {
                title: 'Poor Quality Centers',
                desc: "If your main issue is high turnover because of poor service, marketing won't fix that."
              }, {
                title: 'Passive Owners',
                desc: "If you're just looking for free advice and aren't ready to take action to grow your center."
              }].map((item, i) => <motion.li key={item.title} className="flex items-start gap-4" initial={{
                opacity: 0,
                x: 20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true,
                margin: '-50px'
              }} transition={{
                duration: 0.4,
                delay: i * 0.15,
                ease: 'easeOut'
              }} data-id="element-152">
                    <motion.div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center shrink-0 mt-1" initial={{
                  scale: 0
                }} whileInView={{
                  scale: 1
                }} viewport={{
                  once: true,
                  margin: '-50px'
                }} transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 15,
                  delay: i * 0.15 + 0.2
                }} data-id="element-153">
                      <motion.div className="w-2 h-2 rounded-full bg-red-500" animate={{
                    scale: [1, 1.5, 1],
                    opacity: [1, 0.7, 1]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: i * 0.3
                  }} data-id="element-154" />
                    </motion.div>
                    <div data-id="element-155">
                      <h4 className="text-xl font-bold mb-1" data-id="element-156">{item.title}</h4>
                      <p className="text-zinc-400" data-id="element-157">{item.desc}</p>
                    </div>
                  </motion.li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Lead Capture Form Section */}
      <section className="py-16 md:py-24 relative overflow-hidden" id="apply" data-id="element-158">
        <div className="absolute inset-0 bg-amber-500/5" data-id="element-159"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-160">
          <div className="text-center mb-8 md:mb-12" data-id="element-161">
            <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-162">
              Ready to fill your empty seats?
            </h2>
            <p className="text-xl text-zinc-400" data-id="element-163">
              Apply below to see if your center qualifies for our growth
              program. We only work with one center per 10-mile radius.
            </p>
          </div>

          <div className="rounded-xl border p-6 bg-zinc-900 border-zinc-800 text-white shadow-md shadow-black/20 p-8 md:p-10 shadow-2xl shadow-black/40 relative overflow-hidden" data-id="element-164">
            <div className="absolute top-0 left-0 right-0 h-1 bg-amber-500" aria-hidden="true" data-id="element-165"></div>
            <form className="space-y-4" onSubmit={handleFormSubmit} onFocus={handleFormFocus} aria-label="Strategy call application form" data-id="element-166">
              {isSubmitted ? <div className="text-center py-12" data-id="element-167">
                  <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-168">
                    <CheckCircle2Icon className="h-8 w-8 text-amber-500" data-id="element-169" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3" data-id="element-170">
                    Application Received!
                  </h3>
                  <p className="text-zinc-400 text-lg" data-id="element-171">
                    We'll review your information and reach out within 24 hours.
                  </p>
                </div> : <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-172">
                    <Input label="Full Name" name="full_name" placeholder="Jane Doe" required data-id="element-173" />
                    <Input label="School Name" name="school_name" placeholder="Sunshine Preschool" required data-id="element-174" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-175">
                    <Input label="Work Email" name="email" type="email" placeholder="jane@school.com" required data-id="element-176" />
                    <Input label="Phone Number" name="phone" type="tel" placeholder="(555) 123-4567" required data-id="element-177" />
                  </div>
                  <Select label="Current Enrollment Capacity" name="enrollment_capacity" options={[{
                value: 'under-50',
                label: 'Under 50% Full'
              }, {
                value: '50-75',
                label: '50% - 75% Full'
              }, {
                value: '75-90',
                label: '75% - 90% Full'
              }, {
                value: 'over-90',
                label: 'Over 90% Full (Looking to expand)'
              }]} required data-id="element-178" />
                  {submitError && <p className="text-red-400 text-sm text-center" data-id="element-179">
                      Something went wrong. Please try again.
                    </p>}
                  <div className="pt-4" data-id="element-180">
                    <Button size="lg" fullWidth className="text-lg" onClick={() => handleCtaClick('form_submit')} disabled={isSubmitting} data-id="element-181">
                      {isSubmitting ? 'Sending...' : 'See If You Qualify'}
                    </Button>
                    <p className="text-center text-sm text-zinc-500 mt-4" data-id="element-182">
                      100% secure. We never share your information.
                    </p>
                  </div>
                </>}
            </form>
          </div>
        </div>
      </section>

      {/* 9. Footer */}
      <footer className="bg-black py-12 border-t border-zinc-900" data-id="element-183">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-6" data-id="element-184">
          <a href="#" className="flex items-center" aria-label="SchoolLaunch - Home" data-id="element-185">
            <img src="https://cdn.magicpatterns.com/uploads/3aM3DA6U6f25P9EHkUZYxu/SCHOOL_LAUNCH_LOGO_black_2.png" alt="SchoolLaunch" className="h-8 w-auto" data-id="element-186" />
          </a>
          <div className="text-zinc-500 text-sm" data-id="element-187">
            &copy; {new Date().getFullYear()} School Launch Agency. All rights
            reserved.
          </div>
          <div className="flex gap-6 text-sm text-zinc-400" data-id="element-188">
            <a href="#" className="hover:text-amber-500 transition-colors" data-id="element-189">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-amber-500 transition-colors" data-id="element-190">
              Terms of Service
            </a>
          </div>
        </div>
      </footer>
    </div>;
}